import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@sravni/react-design-system/lib/Button';
import { Space } from '@sravni/react-design-system/lib/Space';
import { useIsMobile } from '@sravni/react-utils';
import { makeProductReviewsPageLink } from '@sravni/ugc/lib/organizations/linkMakers';
import { Components } from '@sravni/vitrina/lib/enums/Components';
import type IRatingComponentConfig from '@sravni/vitrina/lib/types/config/components/IRatingComponentConfig';
import { layouts, ProductInfoPopup } from '@sravni/vitrina-components';

import BranchesMap from '@components/BranchesMap';
import OrganizationInfo from '@components/RatingTable/components/ProductPopup/components/OrganizationInfo';
import Salary from '@components/RatingTable/components/ProductPopup/components/Salary';
import Tabs from '@components/RatingTable/components/ProductPopup/components/Tabs';
import Widgets from '@components/RatingTable/components/ProductPopup/components/Widgets';
import OfferTypes from '@components/RatingTable/components/ProductPopup/enums/offerTypes';
import { findLabelsConfig } from '@components/RatingTable/components/ProductPopup/helpers';
import type { IPixelHandleEventProps } from '@components/RatingTable/components/ProductPopup/helpers/pixelHelper';
import { usePixelHandleEvent } from '@components/RatingTable/components/ProductPopup/hooks/pixel';
import { useFreeButtonAction } from '@components/RatingTable/components/ProductPopup/hooks/useFreeButtonAction';

import { GeoLocation } from '../../../../constants/branches';
import { DEFAULT_RATING_REGION } from '../../../../constants/locations';
import { sendClickButtonInPopupEvent } from '../../../../helpers/analyticsEvents';
import { useFilters } from '../../../../store/hooks/filters';
import { useDictionaries, useProductConfig } from '../../../../store/hooks/productPopupInfo';
import { useAppDispatch } from '../../../../store/hooks/redux';
import { fetchBranches } from '../../../../store/reducers/branches';
import { isServer } from '../../../../utils/isServer';
import queue from '../../../../utils/queue';

import styles from './ProductPopup.module.scss';

type ProductPopupProps = {
  isVisible: boolean;
  onClose: () => void;
  product: any;
};

const ratingConfig: IRatingComponentConfig = {
  component: Components.Rating,
  value: {
    property: 'productRating',
    numberTemplate: '0.00',
  },
};

export const ProductPopup: FC<ProductPopupProps> = ({ isVisible, onClose, product }) => {
  const isMobile = useIsMobile();
  const { location: currentLocation, service, organizationType } = useFilters();
  const productConfig = useProductConfig();
  const dictionaries = useDictionaries();
  const dispatch = useAppDispatch();

  const [location, setLocation] = useState(currentLocation ?? DEFAULT_RATING_REGION);

  useEffect(() => {
    setLocation(location ?? DEFAULT_RATING_REGION);
  }, [location]);

  const {
    list: { desktop, mobile },
    labelsConfig,
    customerType,
  } = productConfig;

  const config = isMobile ? mobile : desktop;
  const { layout: layoutName, components } = config.card;
  const layout = useMemo(() => layouts.getLayout(layoutName, components, isMobile), [layoutName, components, isMobile]);

  const labelConfig = useMemo(() => findLabelsConfig(labelsConfig, product), [labelsConfig, product]);
  const freeButtonAction = useFreeButtonAction();

  useEffect(() => {
    if (isVisible) {
      queue.push(async () =>
        dispatch(
          fetchBranches({
            organizationIds: product?.organization?.alias,
            garRoute: location.routeGar,
            pageSize: 1000,
            customerType,
            organizationType: product?.organization?.type,
            [GeoLocation.latitude]: location.latitude,
            [GeoLocation.longitude]: location.longitude,
          }),
        ),
      );
    }
  }, [location, product?.organization?.alias, product?.organization?.type, isVisible, dispatch, customerType]);

  const link = product.link ?? product.externalLink;

  const footerButtonLabel = isMobile ? product.advertising?.buttonMobileText : product.advertising?.buttonText;

  const handleFooterButtonClick = () => {
    sendClickButtonInPopupEvent({
      organizationName: product.organization?.name.short,
      service,
      productName: product.name,
      buttonLabel: footerButtonLabel,
    });
  };

  const pixelHandleEventProps: IPixelHandleEventProps = {
    advertising: product.advertising,
    product,
    offerType: OfferTypes.Default,
    productName: product.name,
    productSubName: product.subName,
    eventCategory: productConfig.eventCategory,
    isAdvertisingCommon: productConfig.isAdvertisingCommon,
    productLink: link,
    organizationName: product.organization.name.short,
    productId: product.id,
    productType: productConfig.name,
    freeButtonAction,
    onEvent: handleFooterButtonClick,
  };

  const handleAdvBtnClick = usePixelHandleEvent(pixelHandleEventProps);
  const handleReviewsBtnClick = () => {
    const link = makeProductReviewsPageLink({
      organizationAlias: product.organization.alias,
      organizationType,
      productId: product.id,
      serviceAlias: service?.alias,
    });

    document.location.href = link;
  };

  const renderFooter = () => {
    if (isEmpty(product.advertising))
      return (
        <Space direction="horizontal" justify="end">
          <Button className={cn(styles.button)} variant="primary" color="dark" onClick={handleReviewsBtnClick}>
            К отзывам
          </Button>
        </Space>
      );

    return (
      <Space direction="horizontal" justify="end">
        <Button className={cn(styles.button)} variant="primary" color="dark" onClick={handleAdvBtnClick}>
          {footerButtonLabel}
        </Button>
      </Space>
    );
  };

  const renderContent = () => (
    <div className={styles.blockWrapper}>
      {Boolean(config.productInfo.widgets?.length) && (
        <Widgets className={styles.widgets} product={product} iconClassName={styles.icon} iconSize={20} />
      )}
      <Salary product={product} />
      {Boolean(config.productInfo.details?.tabs?.length) && (
        <div className={styles.tabs}>
          <Tabs rowClassName={styles.tabRow} product={product} config={config.productInfo.details.tabs} />
        </div>
      )}
    </div>
  );

  const renderOrganization = () => (
    <OrganizationInfo
      className={cn(styles.accordionContent, styles.accordionTable)}
      product={product}
      contentClassName={styles.organizationContent}
      isPopap
      isAccordion // TODO: Убрать после того как полностью перейдём на аккордеоны
      showHead={false}
    />
  );

  const renderBranches = () => (
    <BranchesMap
      className={cn(styles.dialogMap, styles.accordionContent)}
      headerClassName={styles.mapHeader}
      showHead={false}
      organization={product.organization}
      region={location}
      product={product}
      mapPlacement="Попап"
    />
  );

  return (
    <ProductInfoPopup
      product={product}
      isDeposits={false}
      isMobile={isMobile}
      isServer={isServer}
      isShowDetails={isVisible}
      layout={layout}
      locationV2={location}
      productConfig={productConfig}
      ratingConfig={ratingConfig}
      labelConfig={labelConfig}
      dictionaries={dictionaries}
      onClose={onClose}
      renderContent={renderContent}
      renderBestProduct={() => null}
      renderOrganization={renderOrganization}
      renderFooter={renderFooter}
      renderBranches={renderBranches}
    />
  );
};
